import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, shareReplay } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import { UserModel, UserSelfModel } from '../models/user-management.models';

@Injectable({
  providedIn: 'root',
})
export class UsersManagementService {
  private companyApiUrl = environment.companyApiUrl;
  private userCache$: Observable<UserModel> | null = null;
  private userCacheLite$: Observable<UserModel> | null = null;
  private currentUserId: number | null = null;
  constructor(private http: HttpClient) {}

  //apps and permissions
  getApps() {
    const url = `${this.companyApiUrl}/Apps`;
    return this.http.get(url);
  }

  getPermissions(appId: number, CompanyId?: number) {
    let url = `${this.companyApiUrl}/Apps/${appId}/Permissions`;
    if (CompanyId !== undefined && CompanyId !== null) {
      url += `?companyId=${CompanyId}`;
    }

    return this.http.get(url);
  }

  getRolesByCompany(companyId: number, status?: number, appId?: number) {
    let url = `${this.companyApiUrl}/Roles`;
    const params: string[] = [];

    if (companyId !== undefined) {
      params.push(`CompanyId=${companyId}`);
    }
    if (appId !== undefined) {
      params.push(`AppId=${appId}`);
    }
    if (status !== undefined) {
      params.push(`Status=${status}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    return this.http.get(url);
  }

  getRoleById(id: number) {
    const url = `${this.companyApiUrl}/Roles/${id}`;
    return this.http.get(url);
  }
  getRolePermissions(id: number) {
    const url = `${this.companyApiUrl}/Roles/${id}/Permissions`;
    return this.http.get(url);
  }
  updateRoleById(id: number, permissions: any) {
    const url = `${this.companyApiUrl}/Roles/${id}`;
    return this.http.put(url, permissions);
  }

  createRole(permissions: any) {
    const url = `${this.companyApiUrl}/Roles`;
    return this.http.post(url, permissions);
  }

  deleteRole(id: number) {
    const url = `${this.companyApiUrl}/Roles/${id}`;
    return this.http.delete(url);
  }

  updateUserRoleStatus(id: number, status: number) {
    const url = `${this.companyApiUrl}/Roles/${id}/ChangeStatus`;
    return this.http.put(url, { status: status });
  }

  //users
  getUsersByCompany(
    companyId: number,
    status?: number,
    pageNumber?: number,
    pageSize?: number,
    searchTerm?: string,
  ) {
    let url = `${this.companyApiUrl}/Users`;
    const params: string[] = [];

    if (companyId !== undefined) {
      params.push(`CompanyId=${companyId}`);
    }
    if (status !== undefined) {
      params.push(`Status=${status}`);
    }
    if (pageNumber !== undefined) {
      params.push(`PageNumber=${pageNumber}`);
    }
    if (pageSize !== undefined) {
      params.push(`PageSize=${pageSize}`);
    }
    if (searchTerm !== undefined && searchTerm.trim().length > 0) {
      params.push(`SearchTerm=${encodeURIComponent(searchTerm.trim())}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    return this.http.get(url);
  }

  setDefaultStoreForUser(userId: Number, storeId: any, isDefault: boolean) {
    if (typeof storeId === 'string') {
      storeId = Number(storeId);
    }
    const url = `${this.companyApiUrl}/Users/${userId}/PosStores/${storeId}/Default`;
    return this.http.put(url, { isDefault: isDefault });
  }

  addUser(user: any) {
    const url = `${this.companyApiUrl}/Users`;
    return this.http.post(url, user);
  }

  updateUser(user: any, id: number) {
    const url = `${this.companyApiUrl}/Users/${id}`;
    return this.http.put(url, user);
  }

  getUserById(id: number, isAfterUpdateCall: boolean): Observable<any> {
    if (this.userCache$ && this.currentUserId === id && !isAfterUpdateCall) {
      return this.userCache$;
    }
    const url = `${this.companyApiUrl}/Users/${id}`;
    this.currentUserId = id;
    this.userCache$ = this.http.get<UserModel>(url).pipe(
      shareReplay(1),
      catchError((err) => {
        // Clear cache on error
        this.userCache$ = null;
        this.currentUserId = null;
        throw err;
      }),
    );

    return this.userCache$;
  }

  getUserLiteById(id: number, isAfterUpdateCall: boolean): Observable<any> {
    if (this.userCacheLite$ && this.currentUserId === id && !isAfterUpdateCall) {
      return this.userCacheLite$;
    }
    const url = `${this.companyApiUrl}/Users/${id}/Lite`;
    this.currentUserId = id;
    this.userCacheLite$ = this.http.get<UserModel>(url).pipe(
      shareReplay(1),
      catchError((err) => {
        // Clear cache on error
        this.userCacheLite$ = null;
        this.currentUserId = null;
        throw err;
      }),
    );

    return this.userCacheLite$;
  }

  getUserPermissionsByUserId(userId: number) {
    const url = `${this.companyApiUrl}/Users/${userId}/Permissions`;
    return this.http.get(url);
  }
  deleteUser(id: number) {
    const url = `${this.companyApiUrl}/Users/${id}`;
    return this.http.delete(url);
  }

  removeUserProfile(userId: number) {
    const url = `${this.companyApiUrl}/Users/${userId}/RemoveProfilePicture`;
    return this.http.delete(url);
  }

  addUserProfilePhoto(id: number, file: any, companyId: number, appId?: number) {
    const url = `${this.companyApiUrl}/Users/${id}/UploadProfilePicture?companyId=${companyId}&app=${appId}`;
    return this.http.post(url, file);
  }

  resendInvitation(userId: number) {
    const url = `${this.companyApiUrl}/Users/${userId}/ResendInvitation`;
    return this.http.post(url, {});
  }
  getModulesByApp(appId: number) {
    const url = `${this.companyApiUrl}/Apps/${appId}/Modules`;
    return this.http.get(url);
  }

  updateUserSelfDetails(data: UserSelfModel) {
    const url = `${this.companyApiUrl}/Users/Self/${data.id}`;
    return this.http.put(url, data);
  }

  changePassword(data: any) {
    let userId = data.id;
    const url = `${this.companyApiUrl}/Users/Self/${userId}/ChangePassword`;
    return this.http.post(url, data);
  }

  changeUserStatus(data: any) {
    let userId = data.id;
    const url = `${this.companyApiUrl}/Users/${userId}/ChangeStatus`;
    return this.http.put(url, data);
  }

  resetPasswordAdmin(id: number, password: string) {
    const data = { newPassword: password };
    const url = `${this.companyApiUrl}/Users/${id}/ResetPassword`;
    return this.http.post(url, data);
  }
}
